<template>
  <div class="carousel">
    <div class="carousel-before d-flex flex-wrap justify-content-end gap-1">
      <slot name="before"/>
      <div v-if="buttonNeed" class="carousel_navigation">
        <ButtonComponent
          variant="icon"
          class="left_arrow"
          @btn-click="onLeft()"
          title="swipe left"
        >
          <single-angle/>
        </ButtonComponent>
        <ButtonComponent
            variant="icon"
            class="right_arrow"
            @btn-click="onRightHandle"
            title="swipe right"
        >
          <single-angle/>
        </ButtonComponent>
      </div>
    </div>
    <div
        class="carousel_wrapper"
        ref="itemsListWrapper"
        :style="{
        'scroll-behavior': behaviour,
      }"
        @touchstart="startSwipe"
        @mousedown="startSwipe"
        @touchmove="throttledSwipeProcessing"
        @mousemove.prevent="throttledSwipeProcessing"
        @touchend="endSwipe"
        @mouseleave="endSwipe"
        @mouseup.prevent="endSwipe"
    >
      <div
          class="carousel_items"
          id="carousel_items"
          :style="{
            'grid-template-columns': `repeat(${maxItemInLine}, calc(100%/${itemsOnScreen}))`,
            pointerEvents: itemClickDisabled? 'none' : 'all',
          }"
          @mouseover="onFocus"
          @mouseleave="onBlur"
      >
        <slot v-for="(item, index) in scrolledData" :props="item" :index="index"/>
      </div>
      <slot name="after"/>
    </div>
    <div v-if="showDots && itemData.length > 1" class="carousel_dots">
      <span v-for="item of itemData"
            :key="item.id"
            @click.stop.prevent="swipeTo(item)"
            :class="{
              active: item.id === currentActive.id
            }"
      />
    </div>
  </div>
</template>
<script>
import ButtonComponent from './buttons/ButtonComponent';
import {throttle} from 'lodash';
import SingleAngle from '../../assets/img/singleAngle';

export default {
  name: 'CarouselComponent',
  components: {
    SingleAngle,
    ButtonComponent,
  },
  props: {
    itemData: {
      type: Array,
      required: true,
    },
    scrolledQuantity: {
      type: Number,
      default: 4,
    },
    displayedQuantity: {
      type: Number,
    },
    maxPerLine: {
      type: Number,
      default: 6,
    },
    autoscrollDelay: {
      type: Number,
      default: 5000, // if 0 - autoscroll disabled
    },
    minItemWidth: {
      type: Number,
      required: true,
    },
    maxItemWidth: {
      type: Number,
    },
    needInfinity: {
      type: Boolean,
      default: true,
    },
    showArrow: {
      type: Boolean,
      default: true,
    },
    showDots: {
      type: Boolean,
      default: false,
    },
    integerItemsQuantity: {
      type: Boolean,
      default: true,
    },
    selectedItem: {
      type: Object,
    },
    minMobileCount: {
      type: Number,
      default: 1,
    },
  },
  SCROLL_BEHAVIOUR: {
    SMOOTH: 'smooth',
    AUTO: 'auto',
  },
  data() {
    return {
      scrollLeft: 0,
      itemWidth: null,
      maxScrollLeft: 0,
      autoscrollIntervalId: null,
      itemsOnScreen: this.displayedQuantity,
      beforeDestroyCb: [],
      scrolledData: this.itemData.slice(),
      maxItemInLine: this.maxPerLine,
      behaviour: this.$options.SCROLL_BEHAVIOUR.SMOOTH,
      scrollProcessing: false,
      animateId: null,
      dragAnimateId: null,
      dataLength: this.itemData.length,
      startSwipeX: 0,
      currentSwipeX: 0,
      startSwiping: false,
      currentActive: this.itemData[0],
      itemClickDisabled: false,
    };
  },
  watch: {
    itemData() {
      this.scrolledData = this.itemData.slice();
      this.dataLength = this.itemData.length;
      this.currentActive = this.itemData[0];
      this.scrollLeft = 0;
      this.maxItemInLine = this.maxPerLine;
      this.onResize();
    },
    scrollLeft() {
      this.$refs.itemsListWrapper.scrollLeft = this.scrollLeft;
    },
    itemWidth(newWidth, oldWidth) {
      if (oldWidth) {
        const scrolledQuantity = this.scrollLeft / oldWidth;
        this.setNewLeft(scrolledQuantity * newWidth);
      }
      const widthToCalculate = this.displayedQuantity ? this.minItemWidth : this.maxItemWidth;
      const itemsQuantity =
          this.$refs.itemsListWrapper.clientWidth / widthToCalculate;
      const newItemsQuantity = this.integerItemsQuantity ?
        Math.trunc(itemsQuantity) : itemsQuantity;
      const normalizedItemsQuantity = this.displayedQuantity ? Math.min(
          newItemsQuantity,
          this.displayedQuantity,
      ) : newItemsQuantity;
      this.itemsOnScreen = Math.max(normalizedItemsQuantity, this.minMobileCount);
    },
    itemsOnScreen() {
      this.$nextTick().then(this.onResize);
    },
    async currentSwipeX(newValue, oldValue) {
      if (!this.startSwiping) return;
      if (!oldValue || !newValue) return;
      const delta = oldValue ? newValue - oldValue : newValue - this.startSwipeX;

      if (
        !this.itemClickDisabled &&
        Math.abs(newValue - this.startSwipeX) > this.scrolledWidth * .05
      ) {
        this.itemClickDisabled = true; // To avoid trigger click event after stop scrolling
      }

      if (delta > 0) { // swipe to right
        if (!this.needInfinity && !this.scrollLeft) return;
        if (this.scrollLeft > 0) {
          this.setNewLeft(Math.max(this.scrollLeft - delta, 0));
        } else if (this.itemWidth <= this.maxScrollLeft) { // If there are no scroll
          // left distance add items to start and 'scroll left' value
          // on those items width to imitate infinite scroll
          await this.$nextTick();
          this.setNewLeft(this.scrollLeft + this.scrolledWidth);
          this.replaceItemToStart();
        }
      } else { // swipe to left
        if (!this.needInfinity && this.scrollLeft >= this.maxScrollLeft) {
          return;
        }
        await this.$nextTick();
        this.setNewLeft(Math.min(this.scrollLeft - delta, this.maxScrollLeft));
        await this.$nextTick();

        if (!this.needInfinity) return;
        if (this.scrollLeft >= this.scrolledWidth &&
          Math.floor(this.itemsOnScreen) > this.dataLength) { // if we need infinity scroll
          // and current scrolled width more than width for 1 scroll
          this.setNewLeft(this.scrollLeft - this.scrolledWidth);
          this.needInfinity && this.replaceItemToEnd();
        }
      }
    },
    scrolledData() {
      this.currentActive = this.scrolledData[0];
    },
    selectedItem() {
      this.swipeTo(this.selectedItem);
    },
  },
  computed: {
    normalizedScrolledQuantity() {
      const notDisplayedItems = Math.max(this.dataLength - this.itemsOnScreen, 0);
      const possibleScrollQuantity = Math.min(
          notDisplayedItems,
          this.scrolledQuantity,
      );
      if (this.needInfinity && notDisplayedItems < 1) {
        return 1;
      }
      return Math.min(possibleScrollQuantity, this.itemsOnScreen);
    },
    scrolledWidth() {
      return this.itemWidth * this.normalizedScrolledQuantity;
    },
    buttonNeed() {
      return this.showArrow && this.itemsOnScreen < this.maxItemInLine;
    },
    scrollWidth() {
      return this.itemWidth * this.maxPerLine;
    },
    hasEnoughItemsToSmoothScroll() {
      return (this.itemsOnScreen +
        this.normalizedScrolledQuantity) <= this.dataLength;
    },
  },
  methods: {
    setNewLeft(left) {
      this.scrollLeft = left;
    },
    waitForAnimate(cb) {
      cancelAnimationFrame(this.animateId);
      let coincidences = 2;
      let prevLeft;
      const animate = () => {
        // TODO need optimize
        this.animateId = window.requestAnimationFrame(() => {
          if (typeof prevLeft === 'undefined') {
            prevLeft = 0;
            return animate();
          }

          let ready;
          if (prevLeft && prevLeft > this.$refs.itemsListWrapper.scrollLeft) {
            ready = !this.$refs.itemsListWrapper.scrollLeft;
          } else {
            if (prevLeft === this.$refs.itemsListWrapper.scrollLeft) {
              ready = !coincidences--;
            } else {
              coincidences = 2;
            }
          }
          prevLeft = this.$refs.itemsListWrapper.scrollLeft;
          ready ? cb() : animate();
        });
      };
      animate();
    },
    throttledSwipeProcessing(e) {
      if (!this.startSwiping || this.dragAnimateId) return;
      this.dragAnimateId = window.requestAnimationFrame(() => this.swipeProcessing(e));
    },
    swipeLeft(swipeToData) {
      let scrollWidth = swipeToData?.left ?? this.scrolledWidth;

      if (this.needInfinity || !this.needInfinity && !swipeToData) {
        scrollWidth = this.scrollLeft - scrollWidth;
      }
      const newLeft = Math.max(scrollWidth, 0);
      this.setNewLeft(newLeft);
    },
    swipeRight(swipeToData) {
      let expectedLeft = swipeToData?.left ?? this.scrolledWidth;
      if (this.needInfinity || !this.needInfinity && !swipeToData) {
        expectedLeft += this.scrollLeft;
      }
      const newLeft = Math.min(expectedLeft, this.maxScrollLeft);
      this.setNewLeft(newLeft);
    },
    async infinityRight(swipeToData) {
      if (this.scrollProcessing) return;
      this.scrollProcessing = true;
      this.getMaxScrollLeft();
      await this.$nextTick();
      this.swipeRight(swipeToData);
      this.waitForAnimate(async () => {
        this.behaviour = this.$options.SCROLL_BEHAVIOUR.AUTO;
        await this.$nextTick();
        this.setNewLeft(0);
        this.replaceItemToEnd(swipeToData?.replacedQuantity);
        await this.$nextTick();
        this.behaviour = this.$options.SCROLL_BEHAVIOUR.SMOOTH;
        this.scrollProcessing = false;
      });
    },
    infinityLeft(swipeToData) {
      if (this.scrollProcessing) return;
      this.scrollProcessing = true;
      this.behaviour = this.$options.SCROLL_BEHAVIOUR.AUTO;
      this.$nextTick().then(() => {
        this.replaceItemToStart(swipeToData?.replacedQuantity);
        const additionalWidth = swipeToData ? swipeToData?.left : this.scrolledWidth;
        this.setNewLeft(this.scrollLeft + additionalWidth);
        this.behaviour = this.$options.SCROLL_BEHAVIOUR.SMOOTH;
      }).then(() => this.swipeLeft(swipeToData)).then(() => {
        this.waitForAnimate(() => {
          this.scrollProcessing = false;
        });
      });
    },
    onRight(indexDistance) {
      if (this.maxScrollLeft === 0) return;
      if (this.needInfinity && this.hasEnoughItemsToSmoothScroll) {
        this.infinityRight(indexDistance);
        return;
      }
      if (this.scrollLeft === this.maxScrollLeft) {
        this.setNewLeft(0);
        return;
      }
      this.swipeRight(indexDistance);
    },
    onLeft(distance) {
      if (!this.needInfinity && !this.scrollLeft) return;
      this.stopAutoscroll();
      this.needInfinity && this.hasEnoughItemsToSmoothScroll ?
        this.infinityLeft(distance) : this.swipeLeft(distance);
      this.initAutoscroll();
    },
    onRightHandle() {
      this.stopAutoscroll();
      this.onRight();
      this.initAutoscroll();
    },
    getItemWidth() {
      const wrapperWidth = this.$refs.itemsListWrapper?.clientWidth;
      if (wrapperWidth) {
        this.itemWidth = wrapperWidth / this.itemsOnScreen;
      }
    },
    getMaxScrollLeft() {
      this.maxScrollLeft =
        Math.max(this.scrollWidth -
          this.$refs.itemsListWrapper.clientWidth, 0);
    },
    onResize() {
      this.onFocus();
      this.getItemWidth();
      this.getMaxScrollLeft();
      this.onBlur();
    },
    onFocus() {
      this.stopAutoscroll();
    },
    onBlur() {
      this.initAutoscroll();
    },
    stopAutoscroll() {
      this.autoscrollIntervalId && clearInterval(this.autoscrollIntervalId);
    },
    initAutoscroll() {
      if (!this.autoscrollDelay || Math.ceil(this.itemsOnScreen) === this.dataLength) return false;
      this.stopAutoscroll();
      this.autoscrollIntervalId = setInterval(() => {
        this.onRight();
      }, this.autoscrollDelay);
      this.cleanOnDestroy(() => {
        clearInterval(this.autoscrollIntervalId);
      });
    },
    initFirstCalculate() {
      const el = this.$refs.itemsListWrapper.firstChild;

      if (el.hasChildNodes()) {
        this.onResize();
      } else {
        const observer = new MutationObserver(this.onResize);
        observer.observe(el, {
          childList: true,
        });
        this.cleanOnDestroy(() => observer.disconnect());
      }
    },
    cleanOnDestroy(cb) {
      this.beforeDestroyCb.push(cb);
    },
    replaceItemToStart(replacedQuantity = this.normalizedScrolledQuantity) {
      this.scrolledData = [
        ...this.scrolledData.slice(-replacedQuantity),
        ...this.scrolledData.slice(0, -replacedQuantity),
      ];
    },
    replaceItemToEnd(replacedQuantity = this.normalizedScrolledQuantity) {
      this.scrolledData = [
        ...this.scrolledData.slice(replacedQuantity),
        ...this.scrolledData.slice(0, replacedQuantity),
      ];
    },
    startSwipe(e) {
      if (this.maxScrollLeft === 0) return;
      this.onFocus();
      this.startSwiping = true;
      this.scrollProcessing = true;
      this.startSwipeX = this.currentSwipeX = e.screenX ?? e?.targetTouches?.[0]?.screenX;
      this.behaviour = this.$options.SCROLL_BEHAVIOUR.AUTO;
    },
    async endSwipe() {
      if (!this.startSwiping) return;
      this.startSwiping = false;
      const toLeftDirection = this.startSwipeX > this.currentSwipeX;
      const scrolledContentHideHalf = this.scrollLeft % this.itemWidth > this.itemWidth / 2;
      this.currentSwipeX = null;
      this.startSwipeX = null;
      this.behaviour = this.$options.SCROLL_BEHAVIOUR.SMOOTH;
      this.itemClickDisabled = false;
      await this.$nextTick();
      if (Math.ceil(this.itemsOnScreen) === this.dataLength) return;

      // calculate left offset
      let lastLeft = toLeftDirection && scrolledContentHideHalf? this.itemWidth : 0;
      const scrolledItemCount = Math.round(this.scrollLeft / this.itemWidth);

      if (toLeftDirection) {
        lastLeft = scrolledItemCount * this.itemWidth;
        const lessThanWholeItem = (this.maxScrollLeft - this.scrollLeft) < this.itemWidth;
        if (!this.needInfinity && lessThanWholeItem) { // if there are less than 1 item width left
          lastLeft = this.maxScrollLeft;
        }
      } else {
        lastLeft = Math.round(this.scrollLeft / this.itemWidth) * this.itemWidth;
      }

      if (scrolledContentHideHalf) {
        this.setNewLeft(lastLeft);
        this.waitForAnimate(async () => {
          this.behaviour = this.$options.SCROLL_BEHAVIOUR.AUTO;
          await this.$nextTick();
          if (this.needInfinity) {
            this.setNewLeft(0);
            this.replaceItemToEnd(Math.abs(Math.floor(lastLeft / this.itemWidth)));
          }
          await this.$nextTick();
          this.behaviour = this.$options.SCROLL_BEHAVIOUR.SMOOTH;
          this.scrollProcessing = false;
          this.onBlur();
        });
      } else {
        this.setNewLeft(lastLeft);
        this.waitForAnimate(async () => {
          this.behaviour = this.$options.SCROLL_BEHAVIOUR.AUTO;
          await this.$nextTick();
          if (this.needInfinity) {
            this.setNewLeft(0);
            this.replaceItemToEnd(Math.abs(Math.floor(lastLeft / this.itemWidth)));
          }
          await this.$nextTick();
          this.behaviour = this.$options.SCROLL_BEHAVIOUR.SMOOTH;
          this.scrollProcessing = false;
          this.onBlur();
        });
      }
    },
    swipeProcessing(e) {
      this.dragAnimateId = null;
      if (!this.startSwiping) return;
      this.currentSwipeX = e.screenX || e?.targetTouches?.[0]?.screenX;
    },
    swipeTo(item) {
      this.stopAutoscroll();
      const list = this.showDots? this.itemData : this.scrolledData;
      const currentIndex = list.findIndex((el) => el.id === this.currentActive.id);
      const requestedIndex = list.findIndex((el) => el.id === item.id);
      const indexDistance = requestedIndex - currentIndex;
      const replacedQuantity = Math.abs(indexDistance);
      const left = this.needInfinity?
        this.scrollLeft + indexDistance * this.itemWidth :
        this.itemWidth * requestedIndex;
      const props = {
        left: Math.abs(left),
        replacedQuantity,
      };

      if (!this.needInfinity) {
        this.currentActive = list[requestedIndex];
      }
      const toLeft = this.needInfinity ?
        currentIndex > requestedIndex :
        props.left && this.scrollLeft > props.left;

      if (currentIndex === requestedIndex || this.scrollLeft === props.left) return;
      if (toLeft) {
        this.onLeft(props);
      } else {
        this.onRight(props);
      }
    },
  },
  mounted() {
    this.initFirstCalculate();

    const resizeCb = throttle(this.onResize, 30);
    window.addEventListener('resize', resizeCb);

    this.initAutoscroll();

    this.cleanOnDestroy(() => {
      resizeCb?.cancel?.();
      window.removeEventListener('resize', resizeCb);
    });
  },
  beforeDestroy() {
    this.beforeDestroyCb.forEach((cb) => cb());
    cancelAnimationFrame(this.animateId);
  },
};
</script>

<style scoped lang="scss">
@import "src/assets/css/colors";
@import "src/assets/css/mixins";

.carousel {
  position: relative;
}

.carousel_navigation {
  z-index: 2;
  align-self: flex-end;
  display: flex;
  gap: 1rem;
}

.carousel_wrapper {
  flex-grow: 1;
  position: relative;
  overflow: hidden;
}

.carousel_items {
  display: grid;
  transition: all 0.15s linear;

  & > * {
    overflow: hidden;
  }
}

.left_arrow,
.right_arrow {
  padding: 12px;
  width: 3rem;
  height: 3rem;
  border-radius: 50px;
  background-color: transparent;
  border: 1px solid var(--settings-body-text-color);
  transition: background-color 0.2s linear;

  svg {
    color: var(--settings-body-text-color);
    transition: all 0.2s linear;
  }

  &:hover {
    background-color: var(--settings-body-text-color-10);
  }
}

.right_arrow {
  transform: rotateY(180deg);
}

.carousel_dots {
  display: flex;
  gap: .25rem;
  position: absolute;
  z-index: 1;
  bottom: .5rem;
  right: 1rem;

  span {
    cursor: pointer;
    width: .5rem;
    height: .5rem;
    background-color: var(--settings-header-text-color);
    border-radius: 50%;

    &.active {
      background-color: #008348;
    }
  }
}
</style>
